import { useEffect, useRef } from 'react';
import { useConfiguratorStore } from '@/state/configuratorStore';
import { AdjustHud } from './AdjustHud';

export function Viewport() {
  const containerRef = useRef<HTMLDivElement>(null);
  const textureHostRef = useRef<HTMLDivElement>(null);
  const view = useConfiguratorStore(s => s.view);
  const setView = useConfiguratorStore(s => s.setView);
  const ready = useConfiguratorStore(s => s.ready);
  const error = useConfiguratorStore(s => s.error);
  const view3d = useConfiguratorStore(s => s.view3d);
  const textureFactory = useConfiguratorStore(s => s.textureFactory);
  const selectZone = useConfiguratorStore(s => s.selectZone);
  const placingId = useConfiguratorStore(s => s.placingId);
  const cancelPlace = useConfiguratorStore(s => s.cancelPlace);

  useEffect(() => {
    const container = containerRef.current;
    const host = textureHostRef.current;
    console.info('[viewport] mount effect', {
      hasContainer: !!container,
      hasHost: !!host,
      leadUnlocked: useConfiguratorStore.getState().leadUnlocked,
      containerSize: container ? `${container.clientWidth}x${container.clientHeight}` : null,
      canvases: container ? container.querySelectorAll('canvas').length : null,
    });
    if (!container || !host) {
      console.warn('[viewport] missing refs — init skipped');
      return;
    }
    // Lead gate must open first so boot loading is readable after unlock
    if (!useConfiguratorStore.getState().leadUnlocked) {
      console.info('[viewport] waiting for lead unlock before init');
      return;
    }
    // Only one WebGL canvas may live in the viewport container
    const canvases = Array.from(container.querySelectorAll('canvas'));
    if (canvases.length > 1) {
      console.warn('[viewport] removing extra canvases', canvases.length);
      for (const c of canvases.slice(0, -1)) c.remove();
    }
    void useConfiguratorStore
      .getState()
      .init(container, host)
      .then(() => {
        console.info('[viewport] init promise resolved', {
          ready: useConfiguratorStore.getState().ready,
          error: useConfiguratorStore.getState().error,
          canvases: container.querySelectorAll('canvas').length,
        });
      })
      .catch(err => {
        console.error('[viewport] init promise rejected', err);
      });
    return () => {
      console.info('[viewport] unmount cleanup');
    };
  }, []);

  // Start 3D init once the lead gate unlocks
  const leadUnlocked = useConfiguratorStore(s => s.leadUnlocked);
  useEffect(() => {
    if (!leadUnlocked) return;
    const container = containerRef.current;
    const host = textureHostRef.current;
    if (!container || !host) return;
    if (useConfiguratorStore.getState().view3d) return;
    console.info('[viewport] lead unlocked — starting init');
    void useConfiguratorStore
      .getState()
      .init(container, host)
      .catch(err => console.error('[viewport] init after unlock failed', err));
  }, [leadUnlocked]);

  useEffect(() => {
    if (ready) console.info('[viewport] ready=true, wiring pick handlers');
  }, [ready]);

  useEffect(() => {
    const canvas = containerRef.current?.querySelector('canvas');
    console.info('[viewport] pick effect', {
      ready,
      hasCanvas: !!canvas,
      hasView3d: !!view3d,
      hasTf: !!textureFactory,
    });
    if (!canvas || !ready || !view3d || !textureFactory) return;

    let downX = 0;
    let downY = 0;
    const onDown = (e: PointerEvent) => {
      downX = e.clientX;
      downY = e.clientY;
    };
    const onUp = (e: PointerEvent) => {
      const dx = e.clientX - downX;
      const dy = e.clientY - downY;
      if (dx * dx + dy * dy > 36) return;
      const hit = view3d.screenToUV(e.clientX, e.clientY);
      if (!hit.isPointOnSurface) return;
      const store = useConfiguratorStore.getState();
      // Free placement mode: click anywhere on garment to drop the print
      if (store.placingId) {
        store.placePrintAt(hit.x, hit.y);
        return;
      }
      if (store.pickPrintAt(hit.x, hit.y)) return;
      const zoneId = textureFactory.pickZone(hit.x, hit.y);
      if (zoneId) selectZone(zoneId);
    };

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointerup', onUp);
    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointerup', onUp);
    };
  }, [ready, view3d, textureFactory, selectZone]);

  useEffect(() => {
    if (error) console.error('[viewport]', error);
  }, [error]);

  return (
    <div className={`viewport${placingId ? ' is-placing' : ''}`}>
      <div ref={containerRef} className="viewport__canvas" />
      <div ref={textureHostRef} className="viewport__texture-host" aria-hidden />

      {ready && placingId && (
        <div className="place-overlay" role="status">
          <span>Click the garment to place the print</span>
          <button type="button" onClick={cancelPlace}>
            Cancel
          </button>
        </div>
      )}

      <AdjustHud />

      {ready && (
        <div className="viewport-toolbar" role="toolbar" aria-label="Camera views">
          <button
            type="button"
            className={view === 'front' ? 'is-active' : ''}
            onClick={() => setView('front')}
          >
            Front
          </button>
          <button
            type="button"
            className={view === 'back' ? 'is-active' : ''}
            onClick={() => setView('back')}
          >
            Back
          </button>
          <button
            type="button"
            onClick={() => view3d?.smoothRotateTo(view === 'front' ? 90 : 0)}
          >
            360
          </button>
        </div>
      )}

      {ready && view === 'front' && (
        <button
          type="button"
          className="back-thumb"
          onClick={() => setView('back')}
          aria-label="Show back view"
        >
          <span>BACK</span>
          <div className="back-thumb__placeholder" aria-hidden>
            ↻
          </div>
        </button>
      )}
    </div>
  );
}
